'use client'

import { useState, useEffect, useMemo } from 'react'

import {
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  DndContext,
  rectIntersection,
  DragOverlay,
  defaultDropAnimationSideEffects,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable'
import _ from 'lodash'
import { LoaderCircle } from 'lucide-react'
import { createPortal } from 'react-dom'

import { LevelFooter } from '../../Footer'
import { HUD } from '../../HUD'
import { LevelToolbar } from '../../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { Button } from '@/shared/ui/Button'

import { SortedGameBoard } from './Board'
import { SortedGameGhostWord } from './Word'

interface GameSortedProps {
  step: ChallengeStep
  challengeId: number
}

type ItemsState = {
  correctWords: string[]
  wrongWords: string[]
}

export function GameSorted({ challengeId }: GameSortedProps) {
  const {
    state,
    interactionDisabled,
    goNext,
    enableContinue,
    verifyChallenge,
    retryChallenge
  } = useFlow()
  const { level, journeyAllowWrongAnswers, isSubmitting, onChallengeAnswer } =
    useLevelContext()

  const challenge = level?.active_challenges.find(c => c.id === challengeId)
  const { meta } = challenge
  const correctWords = _.get(meta, 'correct_words', '').split(',')
  const wrongWords = _.get(meta, 'wrong_words', '').split(',')
  const ignoreOrder = _.get(meta, 'ignore_order', false)

  const allWords = useMemo(
    () => _.shuffle(_.concat(correctWords, wrongWords).filter(n => n)),
    [correctWords, wrongWords]
  )

  const [items, setItems] = useState<ItemsState>({
    correctWords: [],
    wrongWords: allWords
  })
  const [activeId, setActiveId] = useState<string | null>(null)
  const [containers, setContainers] = useState<string[]>(Object.keys(items))

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const findContainer = (id: string) => {
    if (id in items) return id
    return Object.keys(items).find(key => items[key].includes(id))
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    const overId = over?.id as string | undefined
    if (overId == null || active.id in items) return
    const overContainer = findContainer(overId)
    const activeContainer = findContainer(active.id as string)
    if (!overContainer || !activeContainer) return

    if (activeContainer !== overContainer) {
      setItems(items => {
        const activeItems = items[activeContainer as keyof ItemsState]
        const overItems = items[overContainer as keyof ItemsState]
        const overIndex = overItems.indexOf(overId)
        const activeIndex = activeItems.indexOf(active.id as string)

        let newIndex
        if (overId in items) {
          newIndex = overItems.length + 1
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height

          const modifier = isBelowOverItem ? 1 : 0
          newIndex =
            overIndex >= 0 ? overIndex + modifier : overItems.length + 1
        }

        return {
          ...items,
          [activeContainer]: items[activeContainer as keyof ItemsState].filter(
            item => item !== active.id
          ),
          [overContainer]: [
            ...items[overContainer as keyof ItemsState].slice(0, newIndex),
            items[activeContainer as keyof ItemsState][activeIndex],
            ...items[overContainer as keyof ItemsState].slice(newIndex)
          ]
        }
      })
    }
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id in items && over?.id) {
      setContainers(containers => {
        const activeIndex = containers.indexOf(active.id as string)
        const overIndex = containers.indexOf(over.id as string)
        return arrayMove(containers, activeIndex, overIndex)
      })
    }

    const activeContainer = findContainer(active.id as string)
    if (!activeContainer) {
      setActiveId(null)
      return
    }

    const overId = over?.id as string | undefined
    if (overId == null) {
      setActiveId(null)
      return
    }
    const overContainer = findContainer(overId)
    if (overContainer) {
      const activeIndex = items[activeContainer as keyof ItemsState].indexOf(
        active.id as string
      )
      const overIndex = items[overContainer as keyof ItemsState].indexOf(overId)
      if (activeIndex !== overIndex) {
        setItems(items => {
          const newItems = {
            ...items,
            [overContainer]: arrayMove(
              items[overContainer as keyof ItemsState],
              activeIndex,
              overIndex
            )
          }
          return newItems
        })
      }
    }
    setActiveId(null)
  }

  const validateAnswer = (correctWordsArray: string[], meta: any) => {
    const expectedCorrectWords = Array.isArray(meta.correct_words)
      ? meta.correct_words
      : (meta.correct_words as string).split(',')

    return ignoreOrder
      ? _.isEqual(
          _.compact(correctWordsArray).sort(),
          _.compact(expectedCorrectWords).sort()
        )
      : _.isEqual(_.compact(correctWordsArray), _.compact(expectedCorrectWords))
  }

  const handleVerify = async () => {
    const answerIsCorrect = validateAnswer(items.correctWords, meta)
    const skipValidation = meta?.save_answer
    const canAnswerChallenge =
      answerIsCorrect || skipValidation || journeyAllowWrongAnswers

    try {
      if (canAnswerChallenge) {
        await onChallengeAnswer({
          uid: challenge.uid,
          answer: items.correctWords.join(', '),
          answerIsCorrect,
          skipValidation
        })
        enableContinue()
      }
      verifyChallenge({ correct: answerIsCorrect || skipValidation })
    } catch (error) {
      showErrorAlert(error)
    }
  }

  const handleRetry = () => {
    setItems({
      correctWords: [],
      wrongWords: allWords
    })
    retryChallenge()
  }

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>
      <HUD.Body className="flex flex-col gap-6">
        <HUD.Description challenge={challenge} />

        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {containers.map(containerId => (
            <SortedGameBoard
              key={containerId}
              id={containerId}
              items={items[containerId as keyof ItemsState]}
              ignoreOrder={ignoreOrder}
              disabled={interactionDisabled}
            />
          ))}
          {createPortal(
            <DragOverlay adjustScale={false} dropAnimation={dropAnimation}>
              <div style={{ touchAction: 'none' }}>
                {activeId ? <SortedGameGhostWord id={activeId} /> : null}
              </div>
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </HUD.Body>

      <LevelFooter>
        {state.phase === 'READY' && (
          <Button
            type="button"
            onClick={handleVerify}
            className="w-full font-bold btn btn-info"
            disabled={items.correctWords.length === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="size-5 animate-spin" />
                <span>Carregando</span>
              </>
            ) : (
              <span>Verificar</span>
            )}
          </Button>
        )}

        {state.phase === 'CORRECT' && (
          <Button
            type="button"
            onClick={goNext}
            className="w-full font-bold btn btn-info"
            disabled={isSubmitting}
          >
            <span>Continuar</span>
          </Button>
        )}

        {state.phase === 'LOCKED_WRONG' && (
          <Button
            type="button"
            onClick={journeyAllowWrongAnswers ? goNext : handleRetry}
            className="w-full font-bold btn btn-info"
            disabled={isSubmitting}
          >
            <span>
              {journeyAllowWrongAnswers ? 'Entendido' : 'Tentar novamente'}
            </span>
          </Button>
        )}
      </LevelFooter>
    </HUD.Card>
  )
}

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5'
      }
    }
  })
}
